// AWS Cognito Hosted UI auth via oidc-client-ts.
// Falls back to a stub (no-op) when Cognito env vars are not set, so the
// app remains usable while the user is still configuring Cognito.

import { UserManager, WebStorageStateStore } from 'oidc-client-ts';
import { clearDocCache } from './docCache';

const region = process.env.REACT_APP_COGNITO_REGION || '';
const userPoolId = process.env.REACT_APP_COGNITO_USER_POOL_ID || '';
const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID || '';
const domain = process.env.REACT_APP_COGNITO_DOMAIN || '';
const redirectUri =
  process.env.REACT_APP_COGNITO_REDIRECT_URI ||
  (typeof window !== 'undefined' ? `${window.location.origin}/auth/callback` : '');
const logoutUri =
  process.env.REACT_APP_COGNITO_LOGOUT_URI ||
  (typeof window !== 'undefined' ? `${window.location.origin}/` : '');

export const isCognitoConfigured = Boolean(
  region && userPoolId && clientId && domain && redirectUri,
);

const authority = isCognitoConfigured
  ? `https://cognito-idp.${region}.amazonaws.com/${userPoolId}`
  : '';

// Cognito does publish OIDC discovery at {authority}/.well-known/openid-configuration,
// but the endpoints there point at the Cognito IDP host, not the Hosted-UI
// `auth.<region>.amazoncognito.com` domain we need for the actual flow. So we
// provide explicit endpoint metadata instead.
const metadata = isCognitoConfigured
  ? {
      issuer: authority,
      authorization_endpoint: `https://${domain}/oauth2/authorize`,
      token_endpoint: `https://${domain}/oauth2/token`,
      userinfo_endpoint: `https://${domain}/oauth2/userInfo`,
      end_session_endpoint: `https://${domain}/logout`,
      jwks_uri: `${authority}/.well-known/jwks.json`,
      revocation_endpoint: `https://${domain}/oauth2/revoke`,
    }
  : null;

export const userManager = isCognitoConfigured
  ? new UserManager({
      authority,
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: 'openid email profile',
      metadata,
      loadUserInfo: false,
      userStore: new WebStorageStateStore({ store: window.localStorage }),
      // Cognito Hosted UI uses query (?code=...&state=...) not hash.
      response_mode: 'query',
      automaticSilentRenew: true,
    })
  : null;

export async function loginRedirect() {
  if (!userManager) return;
  await userManager.signinRedirect({
    // Tell Cognito to immediately federate to Google instead of showing the
    // Hosted UI's account picker. Use `extraQueryParams` per oidc-client-ts.
    extraQueryParams: { identity_provider: 'Google' },
  });
}

export async function logoutRedirect() {
  if (!userManager) return;
  try {
    await userManager.removeUser();
  } catch {}
  // Notes are cached in localStorage — they must not survive into another
  // account's session in this browser.
  clearDocCache();
  // Cognito's /logout endpoint takes its own params, not standard OIDC.
  const url =
    `https://${domain}/logout?client_id=${encodeURIComponent(clientId)}` +
    `&logout_uri=${encodeURIComponent(logoutUri)}`;
  window.location.href = url;
}

export async function handleCallback() {
  if (!userManager) return null;
  return userManager.signinRedirectCallback();
}

export async function getActiveUser() {
  if (!userManager) return null;
  return userManager.getUser();
}

/** Returns the ID token to use as Bearer (matches what Cognito issues with
 *  `openid email profile`; backend accepts both id & access tokens). */
export async function getAccessToken() {
  if (!userManager) return null;
  const user = await userManager.getUser();
  if (!user || user.expired) return null;
  return user.id_token || user.access_token || null;
}
